import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts, setFilters, resetFilters, resetAllFilters } from '../features/product/productListSlice';
import Styles from '../assets/css/productList.module.scss';
import { AiOutlineSearch } from "react-icons/ai";
import { RiFilter2Line } from "react-icons/ri";
import { IoMdClose } from "react-icons/io";
import { BsThreeDotsVertical } from "react-icons/bs";
import { BiEdit } from "react-icons/bi";
import { LiaEyeSolid } from "react-icons/lia";
import { useNavigate, useLocation } from 'react-router-dom';
import Pagination from '../components/common/Pagination';
import Loader from '../utils/Loader';
import ItemImage from '../assets/images/itemImage.png';
import { popUpBoxTrigger } from '../features/message/popUpSlice';

const ProductList = ({ companyId, companyName, filterByCompany = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { products, loading, error, pagination, filters } = useSelector((state) => state.products);

    const [isOpen, setIsOpen] = useState(false);
    const [filterDate, setFilterDate] = useState({
        from: '',
        to: ''
    });
    const [activeDropdown, setActiveDropdown] = useState(null);
    const dropdownRefs = useRef({});
    const filterDropdownRef = useRef(null);
    const filterButtonRef = useRef(null);
    const [dateError, setDateError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [localProductStatus, setLocalProductStatus] = useState({});

    // Reset all filters when navigating between different modules
    useEffect(() => {
        // Only reset filters when coming from a different route (module)
        // If this is the initial load, reset all filters to start fresh
        dispatch(resetAllFilters());

        return () => {
            // When component unmounts, make sure to clear all filters
            // This prevents filters from persisting between different views
            dispatch(resetAllFilters());
        };
    }, [dispatch, location.pathname]);

    // Set company filter on mount if filterByCompany is true
    useEffect(() => {
        if (filterByCompany && companyId) {
            dispatch(setFilters({ company_id: companyId }));
        }
    }, [dispatch, companyId, filterByCompany]);

    // Fetch products whenever filters change
    useEffect(() => {
        dispatch(fetchProducts());
    }, [dispatch, filters]);

    useEffect(() => {
        if (products && products.length > 0) {
            const statusMap = {};

            products.forEach(product => {
                // Use backend product status
                statusMap[product.id] =
                    product.status !== undefined
                        ? product.status
                        : (product.lazada_product?.data?.status === 'Active' ? 1 : 0);
            });

            setLocalProductStatus(statusMap);
        }
    }, [products]);

    // Apply date filter
    const dateStatusApply = () => {
        // Basic date validation
        if ((filterDate.from && !filterDate.to) || (!filterDate.from && filterDate.to)) {
            setDateError("Please select both From and To dates");
            return;
        }

        if (filterDate.from && filterDate.to && new Date(filterDate.from) > new Date(filterDate.to)) {
            setDateError("From date cannot be after To date");
            return;
        }

        // Keep company_id filter when applying date filters if applicable
        const dateFilters = filterByCompany && companyId
            ? {
                from: filterDate.from,
                to: filterDate.to,
                company_id: companyId,
                page: "1"
            }
            : {
                from: filterDate.from,
                to: filterDate.to,
                page: "1"
            };

        dispatch(setFilters(dateFilters));
        setIsOpen(false);
    };

    const handleClear = () => {
        setFilterDate({ from: '', to: '' });
        setDateError('');
    };

    const handleReset = () => {
        setFilterDate({ from: '', to: '' });

        // When resetting, preserve the company filter if we're in company view
        // if (filterByCompany && companyId) {
        //     dispatch(resetFilters({ preserveCompanyId: true }));
        // } else {
        //     dispatch(resetAllFilters());
        // }

        // setIsOpen(false);
        setSearchTerm('');
        setDateError('');
    };

    const handleSearch = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        if (window.searchTimeout) {
            clearTimeout(window.searchTimeout);
        }

        window.searchTimeout = setTimeout(() => {
            // Keep company_id filter when searching if applicable
            const searchFilters = filterByCompany && companyId
                ? { search: value, company_id: companyId, page: "1" }
                : { search: value, page: "1" };

            dispatch(setFilters(searchFilters));
        }, 500);
    };

    const handleDateChange = (type, value) => {
        setFilterDate(prev => ({
            ...prev,
            [type]: value
        }));
        setDateError('');
    };

    const changePage = (count, type) => {
        // Ensure we keep company filter when changing pages if applicable
        const pageFilters = filterByCompany && companyId
            ? { company_id: companyId }
            : {};

        if (type === "page") {
            dispatch(setFilters({
                ...pageFilters,
                page: count.toString()
            }));
        } else if (type === "show") {
            dispatch(setFilters({
                ...pageFilters,
                page: "1",
                paginate_per_page: count.toString()
            }));
        }
    };

    // Fixed dropdown handler
    const handleDropDown = (productId, event) => {
        event.stopPropagation();
        setActiveDropdown(activeDropdown === productId ? null : productId);
    };

    const handleView = (id, event) => {
        event.stopPropagation();
        setActiveDropdown(null);

        if (localProductStatus[id] === 0) {
            dispatch(popUpBoxTrigger({
                type: "error",
                heading: "Product Inactive",
                message: "Product is not active! please check product status",
                show: true
            }));
            return;
        }

        navigate(`/products/view/${id}`);
    };

    const handleEdit = (id, event) => {
        event.stopPropagation();
        setActiveDropdown(null);

        if (localProductStatus[id] === 0) {
            dispatch(popUpBoxTrigger({
                type: "error",
                heading: "Product Inactive",
                message: "Product is not active! please check product status",
                show: true
            }));
            return;
        }

        navigate(`/products/edit/${id}`);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '/');
    };

    // Fixed click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Handle product dropdown closing
            const isDropdownClick = event.target.closest(`.${Styles.manage}`) ||
                event.target.closest(`.${Styles.manageUser}`);

            if (!isDropdownClick) {
                setActiveDropdown(null);
            }

            // Handle filter dropdown closing with better detection
            if (isOpen) {
                const isFilterDropdownClick = filterDropdownRef.current && filterDropdownRef.current.contains(event.target);
                const isFilterButtonClick = filterButtonRef.current && filterButtonRef.current.contains(event.target);
                const isDatePickerClick = event.target.type === 'date' ||
                    event.target.closest('input[type="date"]') ||
                    event.target.closest('.react-datepicker') ||
                    event.target.closest('[data-testid="date-picker"]') ||
                    event.target.closest('.datepicker') ||
                    event.target.closest('.date-picker') ||
                    event.target.closest('.calendar') ||
                    event.target.closest('[role="dialog"]') ||
                    event.target.closest('[role="listbox"]') ||
                    event.target.tagName === 'OPTION' ||
                    (event.target.tagName === 'INPUT' && event.target.type === 'date');

                if (!isFilterDropdownClick && !isFilterButtonClick && !isDatePickerClick) {
                    setIsOpen(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    // Update local state when filters change
    useEffect(() => {
        // Sync local search state with filter state
        if (filters.search !== searchTerm) {
            setSearchTerm(filters.search || '');
        }

        // Sync local date filter state with filter state
        if (filters.from !== filterDate.from || filters.to !== filterDate.to) {
            setFilterDate({
                from: filters.from || '',
                to: filters.to || ''
            });
        }
    }, [filters]);

    // Update filtered products whenever products or filters change
    useEffect(() => {
        if (!loading && products) {
            setFilteredProducts(products);
        }
    }, [products, loading]);

    if (error) {
        return <div className={`${Styles.error}`}>Error: {error}</div>;
    }

    return (
        <div className={`${Styles.container}`}>
            {/* Filter And Search Data */}
            <div className={`${Styles.filter_search_btn}`}>
                <div className={`${Styles.filter}`}>
                    <div className={`${Styles.sub_filter}`}>
                        <button
                            ref={filterButtonRef}
                            onClick={() => { setIsOpen(!isOpen); }}
                            aria-expanded={isOpen}
                        >
                            <RiFilter2Line /> Filter
                        </button>
                        {isOpen && (
                            <div className={`${Styles.filter_dropdown}`} ref={filterDropdownRef}>
                                <div className={`${Styles.heading}`}>
                                    <p>Filter</p>
                                    <IoMdClose onClick={() => setIsOpen(!isOpen)} />
                                </div>

                                <div className={`${Styles.selectDate}`}>
                                    <div className={`${Styles.heading_clear}`}>
                                        <p>Select Date Range</p>
                                        <p onClick={handleClear}>Clear</p>
                                    </div>
                                    <div className={`${Styles.filter_date}`}>
                                        <div className={`${Styles.from}`}>
                                            <label htmlFor="from">From:</label>
                                            <input
                                                type="date"
                                                id='from'
                                                placeholder='00-00-0000'
                                                value={filterDate.from}
                                                max={filterDate.to || undefined}
                                                onChange={(e) => handleDateChange('from', e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <div className={`${Styles.to}`}>
                                            <label htmlFor="to">To:</label>
                                            <input
                                                type="date"
                                                id='to'
                                                placeholder='00-00-0000'
                                                value={filterDate.to}
                                                min={filterDate.from || undefined}
                                                onChange={(e) => handleDateChange('to', e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                    </div>
                                    {dateError && <div className={`${Styles.date_error}`} style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{dateError}</div>}
                                </div>

                                <div className={`${Styles.buttons}`}>
                                    <button onClick={handleReset}>Reset</button>
                                    <button onClick={dateStatusApply}>Apply Now</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${Styles.search}`}>
                    <div className={`${Styles.inputContainer}`}>
                        <AiOutlineSearch className={`${Styles.searchIcon}`} />
                        <input
                            type="text"
                            placeholder="Search by Product Name"
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>
                </div>
            </div>

            {/* Item Table */}
            <div className={`${Styles.product}`}>
                <div className={`${Styles.item_table}`}>
                    {loading ? (
                        <div className={`${Styles.loader_container}`}>
                            <Loader size="large" color="primary" />
                        </div>
                    ) : error ? (
                        <div className={Styles.error_message}>
                            {error}
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className={`${Styles.no_data}`}>
                            {filterByCompany
                                ? `No products found for ${companyName || 'this company'}`
                                : "No products found"}
                        </div>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th className={`${Styles.first_row}`}>No.</th>
                                    <th>Product name</th>
                                    <th>Barcode number</th>
                                    <th>Brand name</th>
                                    <th>Status</th>
                                    <th>Quantity</th>
                                    <th>Created Date</th>
                                    <th aria-label="Actions"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredProducts.map((item, index) => (
                                    <tr key={item.id || index}>
                                        <td className={`${Styles.first_row}`}>
                                            {(pagination.currentPage - 1) * pagination.perPage + index + 1}
                                        </td>
                                        <td>
                                            <div className={`${Styles.item_name}`}>
                                                <img
                                                    src={item.product_image?.[0]?.image_url}
                                                    alt={item.product_name}
                                                    className={`${Styles.item_icon}`}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = ItemImage;
                                                    }}
                                                />
                                                <span className={`${Styles.product_name_text}`}>{item.product_name}</span>
                                            </div>
                                        </td>
                                        <td>{item.barcode_number}</td>
                                        <td>
                                            <div className={`${Styles.brand_name}`}>
                                                <span className={`${Styles.brand_name_text}`}>
                                                    {item.brand_name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className={`${Styles.status_badge} ${item.lazada_product?.data?.status === 'Active' ? Styles.active : Styles.inactive}`}>
                                            {item.lazada_product?.data?.status === 'Active' ? "Active" : "Inactive"}
                                        </td>
                                        <td>{item.quantity}</td>
                                        <td>{formatDate(item.created_at)}</td>
                                        <td>
                                            <div className={`${Styles.manage}`}
                                                ref={el => dropdownRefs.current[item.id] = el}>
                                                <BsThreeDotsVertical
                                                    onClick={(e) => handleDropDown(item.id, e)}
                                                />
                                                {activeDropdown === item.id && (
                                                    <div className={`${Styles.manageUser}`}>
                                                        <p onClick={(e) => handleView(item.id, e)}>
                                                            <span><LiaEyeSolid /></span>
                                                            <span>View</span>
                                                        </p>
                                                        <p onClick={(e) => handleEdit(item.id, e)}>
                                                            <span><BiEdit /></span>
                                                            <span>Edit</span>
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Pagination */}
            {filteredProducts && filteredProducts.length > 0 && (
                <Pagination
                    data={{
                        currentPage: pagination.currentPage,
                        totalPage: pagination.totalPages,
                        showing: pagination.perPage
                    }}
                    pageClick={changePage}
                />
            )}
        </div>
    );
};

export default ProductList;